'use client';

import type { RefObject } from 'react';
import { BaseStatementView } from './base-statement-view';
import { useIncomeStatementData } from '@/hooks/use-income-statement-data';
import type {
  IncomeStatement,
  MetricDefinition,
} from '@/lib/types/financials/statements';

const INCOME_METRICS: MetricDefinition[] = [
  {
    key: 'revenue',
    label: 'Total Revenue',
    category: 'Top Line',
    format: 'currency',
  },
  {
    key: 'costOfRevenue',
    label: 'Cost of Revenue',
    category: 'Top Line',
    format: 'currency',
  },
  {
    key: 'grossProfit',
    label: 'Gross Profit',
    category: 'Profitability',
    format: 'currency',
  },
  {
    key: 'grossProfitRatio',
    label: 'Gross Margin',
    category: 'Margins',
    format: 'percentage',
  },
  {
    key: 'sellingGeneralAndAdministrativeExpenses',
    label: 'SG&A Expenses',
    category: 'Operating Expenses',
    format: 'currency',
  },
  {
    key: 'researchAndDevelopmentExpenses',
    label: 'R&D Expenses',
    category: 'Operating Expenses',
    format: 'currency',
  },
  {
    key: 'operatingIncome',
    label: 'Operating Income',
    category: 'Profitability',
    format: 'currency',
  },
  {
    key: 'operatingIncomeRatio',
    label: 'Operating Margin',
    category: 'Margins',
    format: 'percentage',
  },
  {
    key: 'netIncome',
    label: 'Net Income',
    category: 'Bottom Line',
    format: 'currency',
  },
  {
    key: 'netIncomeRatio',
    label: 'Net Income Margin',
    category: 'Margins',
    format: 'percentage',
  },
  {
    key: 'eps',
    label: 'EPS',
    category: 'Per Share',
    format: 'number',
  },
  {
    key: 'epsdiluted',
    label: 'Diluted EPS',
    category: 'Per Share',
    format: 'number',
  },
];

interface IncomeStatementViewProps {
  symbol: string;
  period: 'annual' | 'quarter';
  years: number;
  chartRef: RefObject<HTMLDivElement | null>;
}

export function IncomeStatementView({
  symbol,
  period,
  years,
  chartRef,
}: IncomeStatementViewProps) {
  const { data, isLoading, error } = useIncomeStatementData({
    symbol,
    period,
    years,
  });

  if (error) {
    return (
      <div className="p-4 border border-red-200 rounded-md bg-red-50 text-red-800">
        <p className="font-medium">Error loading income statement data</p>
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }
  return (
    <BaseStatementView<IncomeStatement>
      data={data}
      isLoading={isLoading}
      chartRef={chartRef}
      metrics={INCOME_METRICS}
      statementType="income"
      currency={data[0]?.reportedCurrency ?? 'USD'}
    />
  );
}
